import { useCallback, useEffect, useState } from "react";

import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

interface FlagDefinition {
  id: string;
  key: string;
  name: string;
  description: string | null;
  status: string;
  rolloutPercent: number;
  rolloutCohort: Record<string, unknown>;
  owner: string | null;
  tags: string[];
  updatedAt: string;
}

interface TenantOption {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface TenantListResponse {
  tenants: TenantOption[];
}

const STATUS_COLORS: Record<string, string> = {
  off: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
  testing: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  rolling_out: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  ga: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  archived: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

const FALLBACK_ENABLED_STATUS = "testing";

function getEnabledStatus(status: string) {
  if (status === "off" || status === "archived") {
    return FALLBACK_ENABLED_STATUS;
  }

  return status;
}

/**
 * D3: Feature Flag Admin Dashboard
 * List flags, create, edit rollout %, cohort, kill switch, audit log.
 */
export default function FeatureFlagsAdminPage() {
  const { t } = useTranslation();
  const [flags, setFlags] = useState<FlagDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FlagDefinition | null>(null);
  const [tenantOverrideFlag, setTenantOverrideFlag] = useState<FlagDefinition | null>(null);

  const fetchFlags = useCallback(async () => {
    setLoadError(null);
    try {
      const data = await apiFetch<{ data: FlagDefinition[] }>("/platform/feature-flags");
      setFlags(data.data);
    } catch {
      setLoadError(t(MSG.admin.featureFlags.unavailableDescription));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchFlags();
  }, [fetchFlags]);

  const handleKillSwitch = async (flagId: string) => {
    await apiFetch(`/platform/feature-flags/${flagId}/kill`, { method: "POST" });
    await fetchFlags();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => (
          <SkeletonCard key={i} className="h-16" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t(MSG.admin.featureFlags.title)}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(MSG.admin.featureFlags.description)}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          {t(MSG.admin.featureFlags.createFlag)}
        </button>
      </div>

      {showCreate && (
        <CreateFlagForm
          onCreated={() => {
            setShowCreate(false);
            void fetchFlags();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {editingFlag && (
        <EditFlagModal
          flag={editingFlag}
          onCancel={() => setEditingFlag(null)}
          onSaved={(updatedFlag) => {
            setEditingFlag(updatedFlag);
            setFlags((currentFlags) =>
              currentFlags.map((flag) => (flag.id === updatedFlag.id ? updatedFlag : flag)),
            );
          }}
        />
      )}

      {loadError ? (
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-base font-semibold">{t(MSG.admin.featureFlags.unavailableTitle)}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
          <button
            onClick={() => {
              setIsLoading(true);
              void fetchFlags();
            }}
            className="mt-4 rounded-md border border-border px-4 py-2 text-sm"
          >
            {t(MSG.admin.featureFlags.retry)}
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-left">
                <th className="px-4 py-3 font-medium">{t(MSG.admin.featureFlags.table.flag)}</th>
                <th className="px-4 py-3 font-medium">{t(MSG.admin.featureFlags.table.status)}</th>
                <th className="px-4 py-3 font-medium">{t(MSG.admin.featureFlags.table.rollout)}</th>
                <th className="px-4 py-3 font-medium">{t(MSG.admin.featureFlags.table.owner)}</th>
                <th className="px-4 py-3 font-medium">{t(MSG.admin.featureFlags.table.updated)}</th>
                <th className="px-4 py-3 font-medium">{t(MSG.admin.featureFlags.table.actions)}</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr key={flag.id} className="stagger-item border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-mono text-xs text-muted-foreground">{flag.key}</p>
                      <p className="font-medium">{flag.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[flag.status] ?? "bg-[var(--color-muted)]"}`}>
                      {flag.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-20 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${flag.rolloutPercent}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{flag.rolloutPercent}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{flag.owner ?? "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {formatDate(flag.updatedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingFlag(flag)}
                        className="rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
                      >
                        {t(MSG.admin.featureFlags.actions.edit)}
                      </button>
                      <button
                        onClick={() => setTenantOverrideFlag(flag)}
                        className="rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
                      >
                        {t(MSG.admin.featureFlags.actions.tenantOverride)}
                      </button>
                      {flag.status !== "off" && (
                        <button
                          onClick={() => void handleKillSwitch(flag.id)}
                          className="rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                        >
                          {t(MSG.admin.featureFlags.actions.kill)}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {flags.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    {t(MSG.admin.featureFlags.empty)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tenantOverrideFlag && (
        <TenantOverridePanel
          flag={tenantOverrideFlag}
          onCancel={() => setTenantOverrideFlag(null)}
        />
      )}
    </div>
  );
}

function TenantOverridePanel({
  flag,
  onCancel,
}: {
  flag: FlagDefinition;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [valueJson, setValueJson] = useState("null");
  const [reason, setReason] = useState("");
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchTenants() {
      setIsLoadingTenants(true);
      setFormError(null);
      try {
        const response = await apiFetch<TenantListResponse>("/platform/tenants?limit=200");
        if (!isMounted) return;
        const nextTenants = response.tenants;
        setTenants(nextTenants);
        const defaultTenant = nextTenants.find((tenant) => tenant.slug === "english-ease") ?? nextTenants[0];
        setSelectedTenantId((current) => current || (defaultTenant?.id ?? ""));
      } catch {
        if (isMounted) {
          setFormError(t(MSG.admin.featureFlags.tenantOverride.loadError));
        }
      } finally {
        if (isMounted) {
          setIsLoadingTenants(false);
        }
      }
    }

    void fetchTenants();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const parseOverrideValue = () => {
    try {
      return { ok: true as const, value: JSON.parse(valueJson) as unknown };
    } catch {
      return { ok: false as const };
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setNotice(null);

    if (!selectedTenantId) {
      setFormError(t(MSG.admin.featureFlags.tenantOverride.tenantRequired));
      return;
    }

    const parsed = parseOverrideValue();
    if (!parsed.ok) {
      setFormError(t(MSG.admin.featureFlags.tenantOverride.valueJsonError));
      return;
    }

    setIsSaving(true);
    try {
      await apiFetch(`/platform/feature-flags/${flag.id}/tenants/${selectedTenantId}`, {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          value: parsed.value,
          reason: reason.trim() || undefined,
        }),
      });
      setNotice(t(MSG.admin.featureFlags.tenantOverride.saveSuccess));
    } catch {
      setFormError(t(MSG.admin.featureFlags.tenantOverride.saveError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    setFormError(null);
    setNotice(null);

    if (!selectedTenantId) {
      setFormError(t(MSG.admin.featureFlags.tenantOverride.tenantRequired));
      return;
    }

    setIsRemoving(true);
    try {
      await apiFetch(`/platform/feature-flags/${flag.id}/tenants/${selectedTenantId}`, {
        method: "DELETE",
        body: JSON.stringify({
          reason: reason.trim() || undefined,
        }),
      });
      setNotice(t(MSG.admin.featureFlags.tenantOverride.removeSuccess));
    } catch {
      setFormError(t(MSG.admin.featureFlags.tenantOverride.removeError));
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">{t(MSG.admin.featureFlags.tenantOverride.title)}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(MSG.admin.featureFlags.tenantOverride.description)}
          </p>
          <p className="mt-2 font-mono text-xs text-muted-foreground">{flag.key}</p>
        </div>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-2 text-sm">
          {t(MSG.admin.featureFlags.tenantOverride.close)}
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="feature-flag-tenant-override-tenant">
            {t(MSG.admin.featureFlags.tenantOverride.tenantLabel)}
          </label>
          <select
            id="feature-flag-tenant-override-tenant"
            name="feature-flag-tenant-override-tenant"
            disabled={isLoadingTenants || tenants.length === 0}
            value={selectedTenantId}
            onChange={(event) => setSelectedTenantId(event.target.value)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm"
          >
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name} ({tenant.slug}) - {tenant.status}
              </option>
            ))}
          </select>
          {isLoadingTenants && (
            <p className="mt-1 text-xs text-muted-foreground">{t(MSG.admin.featureFlags.tenantOverride.loadingTenants)}</p>
          )}
        </div>

        <label className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
          <input
            type="checkbox"
            name="feature-flag-tenant-override-enabled"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span className="text-sm font-medium">{t(MSG.admin.featureFlags.tenantOverride.enabled)}</span>
          <span className="text-xs text-muted-foreground">
            {t(MSG.admin.featureFlags.tenantOverride.enabledHint)}
          </span>
        </label>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="feature-flag-tenant-override-value">
            {t(MSG.admin.featureFlags.tenantOverride.valueLabel)}
          </label>
          <textarea
            id="feature-flag-tenant-override-value"
            name="feature-flag-tenant-override-value"
            value={valueJson}
            onChange={(event) => setValueJson(event.target.value)}
            className="min-h-24 w-full rounded-md border border-input px-3 py-2 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t(MSG.admin.featureFlags.tenantOverride.valueHint)}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="feature-flag-tenant-override-reason">
            {t(MSG.admin.featureFlags.tenantOverride.reasonLabel)}
          </label>
          <input
            id="feature-flag-tenant-override-reason"
            name="feature-flag-tenant-override-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t(MSG.admin.featureFlags.tenantOverride.reasonPlaceholder)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </div>

        {formError && <p className="text-sm text-destructive">{formError}</p>}
        {notice && <p className="text-sm text-[var(--color-success)]">{notice}</p>}

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={isSaving || isLoadingTenants}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
          >
            {isSaving ? t(MSG.admin.featureFlags.tenantOverride.saving) : t(MSG.admin.featureFlags.tenantOverride.save)}
          </button>
          <button
            type="button"
            disabled={isRemoving || isLoadingTenants}
            onClick={() => void handleRemove()}
            className="rounded-md border border-border px-4 py-2 text-sm text-destructive disabled:opacity-50"
          >
            {isRemoving ? t(MSG.admin.featureFlags.tenantOverride.removing) : t(MSG.admin.featureFlags.tenantOverride.remove)}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditFlagModal({
  flag,
  onCancel,
  onSaved,
}: {
  flag: FlagDefinition;
  onCancel: () => void;
  onSaved: (flag: FlagDefinition) => void;
}) {
  const { t } = useTranslation();
  const [description, setDescription] = useState(flag.description ?? "");
  const [enabled, setEnabled] = useState(flag.status !== "off" && flag.status !== "archived");
  const [rolloutPercent, setRolloutPercent] = useState(String(flag.rolloutPercent));
  const [targetingRules, setTargetingRules] = useState(JSON.stringify(flag.rolloutCohort ?? {}, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setJsonError(null);
    setSaveError(null);

    let parsedRules: Record<string, unknown>;
    try {
      const parsed = JSON.parse(targetingRules);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setJsonError(t(MSG.admin.featureFlags.editForm.jsonError));
        return;
      }
      parsedRules = parsed as Record<string, unknown>;
    } catch {
      setJsonError(t(MSG.admin.featureFlags.editForm.jsonError));
      return;
    }

    const parsedRolloutPercent = Number.parseInt(rolloutPercent, 10);
    if (Number.isNaN(parsedRolloutPercent) || parsedRolloutPercent < 0 || parsedRolloutPercent > 100) {
      setSaveError(t(MSG.admin.featureFlags.editForm.rangeError));
      return;
    }

    const nextStatus = enabled ? getEnabledStatus(flag.status) : "off";

    setIsSaving(true);
    try {
      const response = await apiFetch<{ data: FlagDefinition }>(`/platform/feature-flags/${flag.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          description,
          status: nextStatus,
          rolloutPercent: parsedRolloutPercent,
          rolloutCohort: parsedRules,
        }),
      });

      onSaved(response.data);
      onCancel();
    } catch {
      setSaveError(t(MSG.admin.featureFlags.editForm.saveError));
      setIsSaving(false);
    }
  };

  return (
    <div className="mb-6 rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">{t(MSG.admin.featureFlags.editForm.title)}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(MSG.admin.featureFlags.editForm.description)}
          </p>
        </div>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-2 text-sm">
          {t(MSG.admin.featureFlags.editForm.close)}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{t(MSG.admin.featureFlags.editForm.flagName)}</label>
            <input
              readOnly
              value={flag.name}
              className="w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{t(MSG.admin.featureFlags.editForm.flagKey)}</label>
            <input
              readOnly
              value={flag.key}
              className="w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-mono text-muted-foreground"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="feature-flag-description">{t(MSG.admin.featureFlags.editForm.descriptionLabel)}</label>
          <textarea
            id="feature-flag-description"
            name="feature-flag-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="min-h-24 w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </div>

        <label className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
          <input
            type="checkbox"
            name="feature-flag-enabled"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span className="text-sm font-medium">{t(MSG.admin.featureFlags.editForm.enabled)}</span>
          <span className="text-xs text-muted-foreground">
            {t(MSG.admin.featureFlags.editForm.enabledHint)}
          </span>
        </label>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="feature-flag-rollout-percent">
            {t(MSG.admin.featureFlags.editForm.rolloutPercentage)}
          </label>
          <input
            id="feature-flag-rollout-percent"
            name="feature-flag-rollout-percent"
            type="number"
            min={0}
            max={100}
            value={rolloutPercent}
            onChange={(event) => setRolloutPercent(event.target.value)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="feature-flag-targeting-rules">
            {t(MSG.admin.featureFlags.editForm.targetingRules)}
          </label>
          <textarea
            id="feature-flag-targeting-rules"
            name="feature-flag-targeting-rules"
            value={targetingRules}
            onChange={(event) => setTargetingRules(event.target.value)}
            className="min-h-40 w-full rounded-md border border-input px-3 py-2 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t(MSG.admin.featureFlags.editForm.targetingHint)}
          </p>
          {jsonError && <p className="mt-2 text-sm text-destructive">{jsonError}</p>}
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
          >
            {isSaving ? t(MSG.admin.featureFlags.editForm.saving) : t(MSG.admin.featureFlags.editForm.saveChanges)}
          </button>
          <button type="button" onClick={onCancel} className="rounded-md border border-border px-4 py-2 text-sm">
            {t(MSG.admin.featureFlags.editForm.cancel)}
          </button>
        </div>
      </form>
    </div>
  );
}

function CreateFlagForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key || !name) return;
    setIsSubmitting(true);
    try {
      await apiFetch("/platform/feature-flags", {
        method: "POST",
        body: JSON.stringify({ key, name }),
      });
      onCreated();
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-6 rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 font-medium">{t(MSG.admin.featureFlags.createForm.title)}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">{t(MSG.admin.featureFlags.createForm.keyLabel)}</label>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={t(MSG.admin.featureFlags.createForm.keyPlaceholder)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">{t(MSG.admin.featureFlags.createForm.nameLabel)}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(MSG.admin.featureFlags.createForm.namePlaceholder)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {isSubmitting ? t(MSG.admin.featureFlags.createForm.creating) : t(MSG.admin.featureFlags.createForm.create)}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-4 py-2 text-sm">
          {t(MSG.admin.featureFlags.createForm.cancel)}
        </button>
      </div>
    </form>
  );
}
